import { Layout } from '@/components/ui/layout';
import { PrivateAdminRoute } from '@/components/organisms/route/admin';
import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Button, ButtonLoading } from '@/components/ui/button';
import { Add } from '@mui/icons-material';
import { Ellipsis } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PlanManagementDataTable } from '@/pages/planManagement/table/data-table';
import { projectAnalysisAdminColumns } from './table/columns';
import type { ProjectAnalysisAdminRow } from './table/columns';
import { ProjectAnalysisAdminTableLoading } from './table/loading';
import { QUERY_PROJECT_ANALYSIS_REPORTS_ADMIN } from '@/graphql/queries/projectAnalysis';
import {
  MUTATION_ARCHIVE_PROJECT_ANALYSIS_REPORT,
  MUTATION_DELETE_PROJECT_ANALYSIS_REPORT,
} from '@/graphql/mutation/projectAnalysis';
import type { ProjectAnalysisReportAdmin } from '@/graphql/types/projectAnalysis';
import { AppRouters } from '@/utils/constants/routers';

export default function ProjectAnalysisAdminPage() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [data, setData] = useState<ProjectAnalysisReportAdmin[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<ProjectAnalysisReportAdmin | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const loadReports = async () => {
    setLoadingData(true);
    try {
      const { data: res } = await apolloClient.query<{
        projectAnalysisReportsForAdmin: ProjectAnalysisReportAdmin[];
      }>({
        query: QUERY_PROJECT_ANALYSIS_REPORTS_ADMIN,
        fetchPolicy: 'network-only',
      });
      setData(res.projectAnalysisReportsForAdmin);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    void loadReports();
  }, [apolloClient]);

  const handleArchive = async (report: ProjectAnalysisReportAdmin) => {
    try {
      await apolloClient.mutate({
        mutation: MUTATION_ARCHIVE_PROJECT_ANALYSIS_REPORT,
        variables: { report_id: report.id },
      });
      showAlert('success', 'Relatório arquivado');
      await loadReports();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLoadingDelete(true);
    try {
      await apolloClient.mutate({
        mutation: MUTATION_DELETE_PROJECT_ANALYSIS_REPORT,
        variables: { report_id: deleteTarget.id },
      });
      showAlert('success', 'Relatório excluído');
      setDeleteTarget(null);
      await loadReports();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingDelete(false);
    }
  };

  const normalizedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      actions: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem
              onClick={() => navigate(`${AppRouters.projectAnalysisAdmin}/${item.id}`)}
            >
              <span>Editar</span>
            </DropdownMenuItem>
            {item.status === 'PUBLISHED' && (
              <DropdownMenuItem onClick={() => void handleArchive(item)}>
                <span>Arquivar</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-red-600" onClick={() => setDeleteTarget(item)}>
              <span>Excluir</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    })) as ProjectAnalysisAdminRow[];
  }, [data, navigate]);

  return (
    <PrivateAdminRoute>
      <Layout
        title="Análise de projetos"
        customButton={
          <Button onClick={() => navigate(AppRouters.projectAnalysisAdminNew)}>
            <Add />
            Novo relatório
          </Button>
        }
      >
        {loadingData ? (
          <ProjectAnalysisAdminTableLoading />
        ) : (
          <PlanManagementDataTable columns={projectAnalysisAdminColumns} data={normalizedData} />
        )}
      </Layout>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir relatório</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <ButtonLoading
              variant="destructive"
              label="Excluir"
              loading={loadingDelete}
              disabled={loadingDelete}
              onClick={() => void handleDelete()}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PrivateAdminRoute>
  );
}
